/**
 * Owner: Masa Bayu
 * File: batch07-evidence.ts
 * Date:Time: 2026-09-20 00:59
 * Description: Batch 7 evidence packs — regional · READY · not Supported.
 */
import { QX_BATCH_07_EVIDENCE_AREAS, QX_BATCH_07_FAMILY_IDS, QX_BATCH_07_MODEL_VERSION, QX_GL_CERT_GLOBAL_BATCH_07_ID, } from './batch07-codes';
import { fixturesForBatch07Family, listBatch07FixtureIds, QX_BATCH_07_SAMPLE_LANGUAGES, QX_BATCH_07_TEXT_SAMPLES, } from './batch07-fixtures';
import { createQxAccessibilityLanguageSemantics } from './a11y-semantics';
import { allEvidenceTrue, evaluateEvidencePath, } from './certification-evidence';
import { createQxFamilyCertificationRow, supportClaimForRow, } from './certification-row';
import { getCoverageScriptDirectionRow } from './coverage-script-matrix';
import { resolveDirectionHint } from './direction-resolve';
import { QxLanguageError } from './errors';
import { createQxLanguageIdentity } from './identity';
import { assertQxpLanguageMetadataFromQxwOnly, createQxLanguageMetadataBinding, preservePublicationLanguageMetadata, } from './metadata-placement';
import { applyOverrideWithoutChangingContentId, createQxMultilingualContentModel, } from './multilingual-content';
import { roundTripQxMultilingualContentModel, serializeQxMultilingualContentModel, } from './multilingual-serialize';
import { parseQxPublicationLanguageMetadata, serializeQxPublicationLanguageMetadata, } from './metadata-serialize';
import { createQxPublicationLanguageMetadata } from './publication-metadata';
import { resolveLanguageScriptRelation } from './script-relation';
import { createQxTextProcessingProfile, prepareSearchText, } from './search-prep';
import { normalizeTextForSearch } from './text-normalize';
import { resolveTypographyForScript } from './typography-resolve';
import { resolveWritingModeHint } from './writing-mode-resolve';
/** Prior Supported set at Batch 7 READY (through Batch 6) — frozen for claim guards. */
const PRIOR_SUPPORTED_TWENTY_THREE = Object.freeze([
    'latin-core',
    'latin-extended',
    'arabic',
    'hebrew',
    'cyrillic',
    'greek',
    'chinese',
    'japanese',
    'korean',
    'thai',
    'lao',
    'khmer',
    'myanmar',
    'devanagari',
    'bengali',
    'gurmukhi',
    'gujarati',
    'odia',
    'tamil',
    'telugu',
    'kannada',
    'malayalam',
    'sinhala',
]);
const FAMILY_CFG = Object.freeze({
    armenian: Object.freeze({
        scriptId: 'Armn',
        primary: 'hy',
        sampleText: QX_BATCH_07_TEXT_SAMPLES.hy,
        contentId: 'doc-armn-batch07-01',
        analyzer: 'armenian_script',
        shapingRequired: false,
    }),
    georgian: Object.freeze({
        scriptId: 'Geor',
        primary: 'ka',
        sampleText: QX_BATCH_07_TEXT_SAMPLES.ka,
        contentId: 'doc-geor-batch07-01',
        analyzer: 'georgian_script',
        shapingRequired: false,
    }),
    ethiopic: Object.freeze({
        scriptId: 'Ethi',
        primary: 'am',
        sampleText: QX_BATCH_07_TEXT_SAMPLES.am,
        contentId: 'doc-ethi-batch07-01',
        analyzer: 'ethiopic_script',
        shapingRequired: false,
    }),
    tibetan: Object.freeze({
        scriptId: 'Tibt',
        primary: 'bo',
        sampleText: QX_BATCH_07_TEXT_SAMPLES.bo,
        contentId: 'doc-tibt-batch07-01',
        analyzer: 'tibetan_script',
        shapingRequired: true,
    }),
});
function allAreasTrue(areas) {
    for (const key of QX_BATCH_07_EVIDENCE_AREAS) {
        if (!areas[key])
            return false;
    }
    return true;
}
export function buildBatch07FamilyEvidenceBundle(familyId) {
    if (!QX_BATCH_07_FAMILY_IDS.includes(familyId)) {
        throw new QxLanguageError('INVALID_INPUT', `unknown Batch 7 family: ${familyId}`);
    }
    const cfg = FAMILY_CFG[familyId];
    const samples = QX_BATCH_07_SAMPLE_LANGUAGES[familyId];
    const areas = {};
    const fixtures = fixturesForBatch07Family(familyId);
    const matrixRow = getCoverageScriptDirectionRow(familyId);
    const identity = createQxLanguageIdentity({
        languageId: cfg.primary,
        plane: 'publication',
    });
    areas.identity =
        identity.languageId === cfg.primary && samples.includes(cfg.primary);
    const scriptRel = resolveLanguageScriptRelation(cfg.primary);
    areas.script =
        scriptRel.resolvedScriptId === cfg.scriptId &&
            matrixRow.primaryScriptIds.includes(cfg.scriptId) &&
            matrixRow.complexShaping === cfg.shapingRequired &&
            matrixRow.whitespaceTokenizationSafe === true;
    const dir = resolveDirectionHint(cfg.primary);
    const dirOverride = resolveDirectionHint(cfg.primary, 'ltr');
    areas.direction =
        dir.hintedDirection === 'ltr' && dirOverride.override === 'ltr';
    const contentLanguages = samples.length > 1 ? [...samples] : [...samples, 'en'];
    const overrideLang = samples.length > 1 ? samples[1] : 'en';
    const content = createQxMultilingualContentModel({
        contentId: cfg.contentId,
        primaryLanguageId: cfg.primary,
        contentLanguages,
        overrides: [],
    });
    areas.content = content.primaryLanguageId === cfg.primary;
    const pubMeta = createQxPublicationLanguageMetadata({
        primaryLanguageId: cfg.primary,
        contentLanguages,
    });
    const qxwBinding = createQxLanguageMetadataBinding({
        placement: 'qxw_document_root',
        primaryLanguageId: pubMeta.primaryLanguageId,
        defaultScriptId: pubMeta.defaultScriptId,
        defaultDirection: pubMeta.defaultDirection,
        defaultWritingMode: pubMeta.defaultWritingMode,
        contentLanguages: pubMeta.contentLanguages,
    });
    const preserved = preservePublicationLanguageMetadata(qxwBinding.metadata);
    areas.qxw =
        preserved.primaryLanguageId === cfg.primary &&
            preserved.defaultScriptId === cfg.scriptId &&
            preserved.defaultDirection === 'ltr';
    let qxpOk = false;
    try {
        assertQxpLanguageMetadataFromQxwOnly(true);
        const qxp = createQxLanguageMetadataBinding({
            placement: 'qxp_artifact',
            primaryLanguageId: cfg.primary,
            contentLanguages,
        });
        qxpOk =
            qxp.preserveThroughPublish === true &&
                qxp.metadata.defaultDirection === 'ltr';
    }
    catch {
        qxpOk = false;
    }
    areas.qxp = qxpOk;
    const typo = resolveTypographyForScript({
        role: 'body',
        languageId: cfg.primary,
    });
    const heading = resolveTypographyForScript({
        role: 'heading',
        languageId: cfg.primary,
    });
    areas.reader =
        typo.scriptId === cfg.scriptId &&
            heading.scriptId === cfg.scriptId &&
            typo.shapingRequired === cfg.shapingRequired &&
            typo.fallbackChain.length > 0;
    const profile = createQxTextProcessingProfile({ languageId: cfg.primary });
    const prep = prepareSearchText(profile, cfg.sampleText);
    const nfc = normalizeTextForSearch(QX_BATCH_07_TEXT_SAMPLES.composed, 'NFC');
    areas.search =
        profile.analyzerFamily === cfg.analyzer &&
            profile.segmentStrategy === 'whitespace' &&
            prep.segmentation.strategy === 'whitespace' &&
            prep.segmentation.segments.length >= 1 &&
            nfc.normalized === 'café';
    const wm = resolveWritingModeHint(cfg.primary);
    areas.navigation =
        wm.hintedWritingMode === 'horizontal-tb' && heading.role === 'heading';
    areas.annotation = nfc.original !== nfc.normalized && nfc.original.length > 0;
    const a11y = createQxAccessibilityLanguageSemantics({
        contentLanguageId: cfg.primary,
        uiAnnouncementLanguageId: 'en',
        spans: [
            {
                spanId: 'quote-en',
                languageId: 'en',
                boundary: 'inline',
            },
        ],
    });
    areas.accessibility =
        a11y.contentLangTag === cfg.primary &&
            a11y.direction === 'ltr' &&
            a11y.readingOrderPreserved === true &&
            a11y.spanSemantics[0]?.langTag === 'en';
    const metaRt = parseQxPublicationLanguageMetadata(serializeQxPublicationLanguageMetadata(pubMeta));
    const contentRt = roundTripQxMultilingualContentModel(content);
    const json = serializeQxMultilingualContentModel(content);
    areas.serialization =
        metaRt.primaryLanguageId === cfg.primary &&
            metaRt.defaultDirection === 'ltr' &&
            contentRt.contentId === cfg.contentId &&
            typeof json.modelVersion === 'string';
    const next = applyOverrideWithoutChangingContentId(content, {
        scope: 'block',
        spanId: `note-${overrideLang}`,
        languageId: overrideLang,
    }, cfg.contentId);
    areas.regression =
        next.contentId === content.contentId && next.overrides.length === 1;
    const provenance = fixtures.map((f) => Object.freeze({ fixtureId: f.id, area: f.area }));
    const areaSet = new Set(QX_BATCH_07_EVIDENCE_AREAS);
    areas.provenance =
        provenance.length === QX_BATCH_07_EVIDENCE_AREAS.length &&
            provenance.every((p) => areaSet.has(p.area));
    const frozenAreas = Object.freeze({ ...areas });
    const pathEvidence = allEvidenceTrue();
    const pathEval = evaluateEvidencePath(pathEvidence);
    const areasPass = allAreasTrue(frozenAreas);
    const row = createQxFamilyCertificationRow({
        familyId,
        scriptIds: [cfg.scriptId],
        sampleLanguageIds: [...samples],
        directions: ['ltr'],
        level: 'experimental',
        evidence: pathEvidence,
        evidenceRefs: listBatch07FixtureIds(familyId),
        gaps: ['awaiting_owner_accept_for_certified', 'batch_open_not_supported'],
        ownerAcceptedAt: null,
    });
    const claimStatus = supportClaimForRow(row);
    if (claimStatus === 'supported') {
        throw new QxLanguageError('INVALID_INPUT', `${familyId} must not claim Supported before Owner Accept`);
    }
    const ready = areasPass && pathEval.status === 'pass';
    return Object.freeze({
        modelVersion: QX_BATCH_07_MODEL_VERSION,
        batchId: QX_GL_CERT_GLOBAL_BATCH_07_ID,
        familyId,
        scriptId: cfg.scriptId,
        areas: frozenAreas,
        pathEvidence,
        pathStatus: pathEval.status,
        fixtures: listBatch07FixtureIds(familyId),
        provenance: Object.freeze(provenance),
        row,
        supportedClaimAllowed: false,
        claimStatus,
        readyForOwnerAccept: ready,
        automatedTestsPass: ready,
        manualQaChecklistPackaged: true,
        a11ySpotCheckPass: areas.accessibility,
        notes: ready
            ? `${familyId} Batch 7 regional evidence READY — not Supported`
            : `${familyId} Batch 7 evidence incomplete`,
    });
}
export function buildBatch07EvidenceReport() {
    const families = {};
    for (const id of QX_BATCH_07_FAMILY_IDS) {
        families[id] = buildBatch07FamilyEvidenceBundle(id);
    }
    const ready = QX_BATCH_07_FAMILY_IDS.every((id) => families[id].readyForOwnerAccept);
    return Object.freeze({
        batchId: QX_GL_CERT_GLOBAL_BATCH_07_ID,
        modelVersion: QX_BATCH_07_MODEL_VERSION,
        families: Object.freeze(families),
        readyForOwnerAccept: ready,
        batchOpenImpliesSupported: false,
        supportedFamilyIdsStill: PRIOR_SUPPORTED_TWENTY_THREE,
    });
}
//# sourceMappingURL=batch07-evidence.js.map